import Vue, { defineComponent } from 'vue';
import i18n from '@/common/i18n';
import { store } from '@/common/root';
import applyFont from '@/common/utils/applyFont';
import setHighchartsLocale from '@/common/utils/highcharts';
import '@/common/ui';
import { abilitiesPlugin } from '@casl/vue';
import ability from '@/common/ability';
import { mapStores } from 'pinia';
import useI18nStore from '@/common/store/pinia/i18n';
import usePostMessage from '@/common/composables/usePostMessage';
import useAuthStore from '@/common/store/pinia/auth';

Vue.use(abilitiesPlugin, ability);
const { sendPostMessage } = usePostMessage();

export default defineComponent({
  i18n,
  store,
  mounted() {
    sendPostMessage('init');
    document.addEventListener('keydown', this.keyListener.bind(this));
    // NOTE (mpetrov): intentionally trigger disconnect event to free up notifier session on the backend side
    document.addEventListener('beforeunload', this.socketDisconnect);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keyListener);
    this.socketDisconnect();
  },
  computed: {
    ...mapStores(useI18nStore, useAuthStore),
    currentLocale(): string {
      return i18n.locale;
    },
    locales(): string[] {
      return this.i18nStore.getEnabledLanguages.map(l => l.language);
    }
  },
  watch: {
    currentLocale(locale: string) {
      applyFont(locale);
      setHighchartsLocale();
    },
    '$route.query.project_id': {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.authStore.setProject(newVal);
        }
      }
    },
    '$route.query.locale': {
      immediate: true,
      handler(newVal) {
        if (typeof newVal === 'string'
          && this.currentLocale !== newVal
          && this.locales.includes(newVal)) {
          this.i18nStore.loadLanguage(newVal);
        }
      }
    }
  },
  methods: {
    socketDisconnect() {
      this.$store.dispatch('socket/DISCONNECT');
    },
    changeLanguage(isReverse): PromiseLike<unknown> {
      const { locales } = this;
      const locale = locales[
        (locales.indexOf(this.currentLocale) + (isReverse ? -1 : 1) + locales.length) % locales.length
      ];
      return this.i18nStore.loadLanguage(locale);
    },
    keyListener(e) {
      if (e.code === 'KeyL' && (e.ctrlKey)) {
        e.preventDefault();

        this.changeLanguage(e.shiftKey);
      }
    }
  }
});
